import * as tfc from '@tensorflow/tfjs-core';
import * as tfconv from '@tensorflow/tfjs-converter';
import * as tfjsWasm from '@tensorflow/tfjs-backend-wasm';
import { getSimilarityTransformation } from '../../helpers/dl_allignment';

const OpenCvLoadedTimeoutInMs = 100;

export const load = async () => {
  tfjsWasm.setWasmPath('/tfjs-backend-wasm.wasm');
};

export const init = async (data) => {
  const { backend, maskDetectionsSettings } = data;

  await tfc.setBackend(backend);
  const [maskModel] = await Promise.all([
    tfconv.loadGraphModel(
      `/rgb_mask_classification_first/MobileNetV${maskDetectionsSettings.mobileNetVersion}_${maskDetectionsSettings.mobileNetWeight}/${maskDetectionsSettings.mobileNetType}/model.json`,
    ),
    isOpenCvLoaded(),
  ]);

  return maskModel;
};

export const detectMask = async (data, maskModel) => {
  const {
    predictions,
    imageDataToProcess,
    thumbnailShortTransformation,
    thumbnailSizeForMask,
    faceDetectionImageWidth,
    thumbnailFullTransformation,
    imageTransformType,
    lastIndex,
  } = data;

  let masksScores = [];
  let imgData = null;

  for (let i = 0; i < predictions.length; i++) {
    const landmarks = predictions[i].landmarks;

    for (let j = 0; j < landmarks.length; j++) {
      landmarks[j][0] = faceDetectionImageWidth - landmarks[j][0];
    }

    // eslint-disable-next-line no-undef
    const srcImageData = cv.matFromImageData(imageDataToProcess);
    // eslint-disable-next-line no-undef
    let dstImageData = new cv.Mat();
    // eslint-disable-next-line no-undef
    let thumbnailSize = new cv.Size(thumbnailSizeForMask, thumbnailSizeForMask);

    let canvas = new OffscreenCanvas(thumbnailSizeForMask, thumbnailSizeForMask);

    if (imageTransformType === 'short') {
      // eslint-disable-next-line no-undef
      const blaze_src = cv.matFromArray(3, 1, cv.CV_32FC2, [
        landmarks[1][0],
        landmarks[1][1],
        landmarks[0][0],
        landmarks[0][1],
        landmarks[2][0],
        landmarks[2][1],
      ]);
      // eslint-disable-next-line no-undef
      const blaze_dst = cv.matFromArray(
        3,
        1,
        cv.CV_32FC2,
        thumbnailShortTransformation[thumbnailSizeForMask.toString()],
      );
      // eslint-disable-next-line no-undef
      let affineTransformation = cv.getAffineTransform(blaze_src, blaze_dst);

      // eslint-disable-next-line no-undef
      cv.warpAffine(
        srcImageData,
        dstImageData,
        affineTransformation,
        thumbnailSize,
        cv.INTER_LINEAR,
        cv.BORDER_CONSTANT,
        new cv.Scalar(),
      );

      imgData = new ImageData(
        new Uint8ClampedArray(dstImageData.data, dstImageData.cols, dstImageData.rows),
        thumbnailSizeForMask,
        thumbnailSizeForMask,
      );
      canvas.width = imgData.width;
      canvas.height = imgData.height;
      let ctx = canvas.getContext('2d');
      ctx.putImageData(imgData, 0, 0);

      const prediction = tfc.tidy(() => {
        let maskDetectionSnapshotFromPixels = tfc.browser.fromPixels(canvas);

        let maskDetectionSnapshotFromPixelsFlot32 = tfc.cast(maskDetectionSnapshotFromPixels, 'float32');
        let expandedDims = maskDetectionSnapshotFromPixelsFlot32.expandDims(0);

        return maskModel.predict(expandedDims);
      });

      masksScores.push(prediction.dataSync()[0].toFixed(4));

      affineTransformation.delete();
      blaze_dst.delete();
      blaze_src.delete();
      dstImageData.delete();
      srcImageData.delete();
      prediction.dispose();
    } else if (imageTransformType === 'full') {
      // eslint-disable-next-line no-undef
      let similarityTransformation = getSimilarityTransformation(
        landmarks,
        thumbnailFullTransformation[thumbnailSizeForMask.toString()],
      );
      // eslint-disable-next-line no-undef
      let similarityTransformationMatrix = cv.matFromArray(3, 3, cv.CV_64F, similarityTransformation.data);

      // eslint-disable-next-line no-undef
      cv.warpPerspective(
        srcImageData,
        dstImageData,
        similarityTransformationMatrix,
        thumbnailSize,
        cv.INTER_LINEAR,
        cv.BORDER_CONSTANT,
        new cv.Scalar(),
      );

      imgData = new ImageData(
        new Uint8ClampedArray(dstImageData.data, dstImageData.cols, dstImageData.rows),
        thumbnailSizeForMask,
        thumbnailSizeForMask,
      );
      canvas.width = imgData.width;
      canvas.height = imgData.height;
      let ctx = canvas.getContext('2d');
      ctx.putImageData(imgData, 0, 0);

      const prediction = tfc.tidy(() => {
        let maskDetectionSnapshotFromPixels = tfc.browser.fromPixels(canvas);

        let maskDetectionSnapshotFromPixelsFlot32 = tfc.cast(maskDetectionSnapshotFromPixels, 'float32');
        let expandedDims = maskDetectionSnapshotFromPixelsFlot32.expandDims(0);

        return maskModel.predict(expandedDims);
      });

      masksScores.push(prediction.dataSync()[0].toFixed(4));

      similarityTransformationMatrix.delete();
      dstImageData.delete();
      srcImageData.delete();
      prediction.dispose();
    } else {
      throw new Error(`It is specified not supported transformation=${imageTransformType}`);
    }

    return {
      imgData,
      masksScores,
      lastIndex,
    };
  }
};

export const isOpenCvLoaded = () => {
  let timeoutId;

  const resolveOpenCvPromise = (resolve) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    try {
      // eslint-disable-next-line no-undef
      if (cv && cv.Mat) {
        return resolve();
      } else {
        timeoutId = setTimeout(() => {
          resolveOpenCvPromise(resolve);
        }, OpenCvLoadedTimeoutInMs);
      }
    } catch {
      timeoutId = setTimeout(() => {
        resolveOpenCvPromise(resolve);
      }, OpenCvLoadedTimeoutInMs);
    }
  };

  return new Promise((resolve) => {
    resolveOpenCvPromise(resolve);
  });
};
